import { type StateType } from '../../states';
import { type UserState } from '../../states/user';
import {
  type UserJwtState,
  type UserBankrollVariationState,
  type UserPresetItemState,
  type UserMilesState,
  type UserBonusState,
  type UserTicketsState,
} from '../../states/user';
import { type UserBankrollEntityVariationState } from '../../states/user';
import { type UserPrefsRouteState, type UserPrefsCalendarRouteState } from '../../states/user';
import { type UserPrefsState } from '../../states/user';
import { type BalanceChangeOrigin } from '../../types/balanceChanges';
import { type BalanceChangeType } from '../../types/balanceChanges';
import { PromotionStatuses, type PromotionStatus } from '../../types/promotions';
import { type Selector } from '../../types/selectors';

const emptyArr: [] = [];

const selectUserState: Selector<UserState> = (state: StateType) => state.user;

export const selectUserConnected: Selector<boolean> = (state: StateType) => selectUserState(state)?.connected ?? false;

export const selectUserConnecting: Selector<boolean> = (state: StateType) =>
  selectUserState(state)?.connecting ?? false;

export const selectUserJwt: Selector<UserJwtState | null> = (state: StateType) => selectUserState(state)?.jwt;

export const selectUserJwtToken: Selector<string | undefined> = (state: StateType) => selectUserJwt(state)?.token;

export const selectUserJwtExpirationDelay: Selector<number | undefined> = (state: StateType) =>
  selectUserJwt(state)?.expirationDelay;

export const selectUserDidReceivedUserData: Selector<boolean> = (state: StateType) =>
  selectUserState(state)?.didReceivedUserData ?? false;

export const selectUserNewuser: Selector<boolean> = (state: StateType) => selectUserState(state)?.newuser;

export const selectUserNickname: Selector<string | null | undefined> = (state: StateType) =>
  selectUserState(state)?.nickname;

export const selectUserBalance: Selector<number> = (state: StateType) => selectUserState(state)?.balance ?? 0;

export const selectUserBalanceCash: Selector<number | null | undefined> = (state: StateType) =>
  selectUserState(state)?.balanceCash;

export const selectUserBalanceBonus: Selector<number | null | undefined> = (state: StateType) =>
  selectUserState(state)?.balanceBonus;

export const selectUserFreebetBankroll: Selector<number | undefined> = (state: StateType) =>
  selectUserState(state)?.freebetBankroll ?? 0;

export const selectUserBalanceDiff: Selector<number | null> = (state: StateType) => selectUserState(state)?.balanceDiff;

export const selectUserBankrollVariation: Selector<UserBankrollVariationState | undefined> = (state: StateType) =>
  selectUserState(state)?.bankrollVariation;

export const selectUserBankrollVariationBalance: Selector<UserBankrollEntityVariationState | undefined> = (
  state: StateType,
) => selectUserBankrollVariation(state)?.balance;

export const selectUserBankrollVariationBalanceLastChangeOrigin: Selector<BalanceChangeOrigin | null | undefined> = (
  state: StateType,
) => selectUserBankrollVariationBalance(state)?.lastChangeOrigin;

export const selectUserBankrollVariationBalanceLastChangeTs: Selector<number | null | undefined> = (state: StateType) =>
  selectUserBankrollVariationBalance(state)?.lastChangeTs;

export const selectUserBankrollVariationBalanceLastChangeType: Selector<BalanceChangeType | null | undefined> = (
  state: StateType,
) => selectUserBankrollVariationBalance(state)?.lastChangeType;

export const selectUserBankrollVariationBalanceShouldAnimateBankroll: Selector<boolean> = (state: StateType) =>
  selectUserBankrollVariationBalance(state)?.shouldAnimateBankroll ?? false;

export const selectUserBankrollVariationFreebets: Selector<UserBankrollEntityVariationState | undefined> = (
  state: StateType,
) => selectUserBankrollVariation(state)?.freebets;

export const selectUserBankrollVariationFreebetsLastChangeOrigin: Selector<BalanceChangeOrigin | null | undefined> = (
  state: StateType,
) => selectUserBankrollVariationFreebets(state)?.lastChangeOrigin;

export const selectUserBankrollVariationFreebetsLastChangeTs: Selector<number | null | undefined> = (
  state: StateType,
) => selectUserBankrollVariationFreebets(state)?.lastChangeTs;

export const selectUserBankrollVariationFreebetsLastChangeType: Selector<BalanceChangeType | null | undefined> = (
  state: StateType,
) => selectUserBankrollVariationFreebets(state)?.lastChangeType;

export const selectUserBankrollVariationFreebetsShouldAnimateBankroll: Selector<boolean> = (state: StateType) =>
  selectUserBankrollVariationFreebets(state)?.shouldAnimateBankroll ?? false;

export const selectUserPresets: Selector<Array<UserPresetItemState> | null> = (state: StateType) =>
  selectUserState(state)?.presets;

export const selectUserLastSessionSettledBets: Selector<Array<string>> = (state: StateType) =>
  selectUserState(state).lastSessionSettledBets ?? emptyArr;

export const selectUserEmbeddedNotificationTarget: Selector<string | undefined> = (state: StateType) =>
  selectUserState(state)?.embeddedNotificationTarget;

export const selectUserMustValidateCGU: Selector<boolean> = (state: StateType) =>
  selectUserState(state)?.mustValidateCGU ?? false;

export const selectUserMustValidateEvents: Selector<boolean> = (state: StateType) =>
  selectUserState(state)?.mustValidateEvents ?? false;

export const selectUserMessages: Selector<Array<string>> = (state: StateType) =>
  selectUserState(state)?.messages ?? emptyArr;

export const selectUserMiles: Selector<UserMilesState> = (state: StateType) => selectUserState(state)?.miles;

export const selectUserBonuses: Selector<Array<UserBonusState> | undefined> = (state: StateType) =>
  selectUserState(state)?.bonuses;

export const selectUserTickets: Selector<UserTicketsState | undefined> = (state: StateType) =>
  selectUserState(state)?.tickets;

export const selectUserPrefs: Selector<UserPrefsState> = (state: StateType) => selectUserState(state).prefs;

export const selectUserCalendarPrefs: Selector<UserPrefsCalendarRouteState> = (state: StateType) =>
  selectUserPrefs(state).calendar;

export const selectUserFavoritesPrefs: Selector<UserPrefsRouteState> = (state: StateType) =>
  selectUserPrefs(state).favorites;

export const selectUserLivePrefs: Selector<UserPrefsRouteState> = (state: StateType) => selectUserPrefs(state).live;

export const selectUserPrefsShowWTV: Selector<boolean> = (state: StateType) => selectUserPrefs(state).showWTV ?? false;

export const selectUserCC: Selector<string | null> = (state: StateType) => selectUserState(state).cc;

export const selectUserSessionId: Selector<string | null> = (state: StateType) => selectUserState(state).sessionId;

export const selectUserPromotionStatus: Selector<PromotionStatus> = (state: StateType) =>
  selectUserState(state)?.promotionStatus ?? PromotionStatuses.DEFAULT;

export const selectUserMissionsModerator: Selector<boolean> = (state: StateType) =>
  selectUserState(state)?.missionsModerator ?? false;

export const selectUserOddsTravelEnabled: Selector<boolean> = (state: StateType) =>
  selectUserState(state)?.oddsTravelEnabled ?? true;

export const selectUserShowLineUpNotificationsInfosPopUp: Selector<boolean> = (state: StateType) =>
  selectUserState(state)?.showLineUpNotificationsInfosPopUp ?? false;

export const selectUserDisplayBetSliders: Selector<boolean> = (state: StateType) =>
  selectUserState(state)?.displayBetSliders ?? false;

export const selectUserRealMoney: Selector<boolean> = (state: StateType) => selectUserState(state).realMoney ?? false;

export const selectUserGodfatherAmount: Selector<number | undefined> = (state: StateType) =>
  selectUserState(state).godfatherAmount;

export const selectUserGodsonAmount: Selector<number | undefined> = (state: StateType) =>
  selectUserState(state).godsonAmount;
